import {
  IconCalendarClockOutline,
  IconChecklistOutline,
  IconChevronRightOutline,
  IconCloseCircleOutline,
  IconCopyOutline,
  IconDeleteOutline,
  IconDownloadOutline,
  IconDrawOutline,
  IconEditOutline,
  IconExternalOutline,
  IconFileOutline,
  IconLinkBrokenOutline,
  IconPiggyBankOutline,
  IconSendOutline,
  IconShareWebOutline,
  IconSignatureOutline,
} from '@zillow/constellation-icons';

import { MOBILE_HANDOFF_TYPES } from '../../../constants/stringConstants';

import {
  emitCancelSigningEvent,
  emitDeactivateLeaseEvent,
  emitDeleteDraftEvent,
  emitDownloadSignedPdfEvent,
  emitDuplicateLeaseEvent,
  emitEditZdocsEvent,
  emitFinishCreatingLeaseEvent,
  emitNextBottomEvent,
  emitNextTopEvent,
  emitPreviewFullLeaseBottomEvent,
  emitPreviewFullLeaseTopEvent,
  emitRequestSignatureZdocsEvent,
  emitResendSigningEmailsEvent,
  emitSetupRentPaymentEvent,
  emitViewAndSignLeaseEvent,
  emitViewLeaseEvent,
} from '../../../../analytics/events';
import type { LeaseListingUserEventArgs } from '../../../../analytics/events';

// https://bitbucket.hotterpads.com/projects/RP/repos/leasing/browse/leasing/
//     src/main/java/com/hotpads/leasing/model/enums/LeaseAction.java

// *** THE DEFINITIONS HERE ARE USED DIRECTLY IN ANALYTICS TRACKING. BE CAREFUL WHEN MODIFYING ***
export enum LEASE_ACTION_TYPES {
  ARCHIVE_LEASE = 'archive_lease',
  ARCHIVE_LEASE_EXTENSION = 'archive_lease_extension',
  BEGIN_COUNTERSIGN_LEASE = 'begin_countersign_lease',
  CANCEL_SIGNING = 'cancel_signing',
  CANCEL_SIGNING_LEASE_EXTENSION = 'cancel_signing_lease_extension',
  COPY_LEASE = 'copy_lease',
  COUNTERSIGN_LEASE = 'countersign_lease',
  COUNTERSIGN_LEASE_EXTENSION = 'countersign_lease_extension',
  CREATE_PROPERTY_CONDITION_REPORT = 'create_property_condition_report',
  DEACTIVATE_LEASE = 'deactivate_lease',
  DELETE_LEASE = 'delete_lease',
  DOWNLOAD_DOCUMENT = 'download_document',
  DOWNLOAD_DOCUMENT_LEASE_EXTENSION = 'download_document_lease_extension',
  DOWNLOAD_DRAFT_PDF = 'download_draft_pdf',
  DOWNLOAD_DRAFT_PDF_LEASE_EXTENSION = 'download_draft_pdf_lease_extension',
  DOWNLOAD_SHARE_COMPLETE_DRAFT = 'download_share_complete_draft',
  DOWNLOAD_SHARE_INCOMPLETE_DRAFT = 'download_share_incomplete_draft',
  DOWNLOAD_SHARE_COMPLETE_DRAFT_LEASE_EXTENSION = 'download_share_complete_draft_lease_extension',
  DOWNLOAD_SHARE_INCOMPLETE_DRAFT_LEASE_EXTENSION = 'download_share_incomplete_draft_lease_extension',
  DOWNLOAD_SIGNABLE_PDF = 'download_signable_pdf',
  DOWNLOAD_SIGNED_PDF = 'download_signed_pdf',
  DOWNLOAD_SIGNED_PDF_LEASE_EXTENSION = 'download_signed_pdf_lease_extension',
  EDIT_LEASE = 'edit_lease',
  EDIT_LEASE_EXTENSION = 'edit_lease_extension',
  EDIT_SIGNATURES = 'edit_signatures',
  EDIT_VIEW_LEASE = 'edit_view_lease',
  EDIT_VIEW_LEASE_EXTENSION = 'edit_view_lease_extension',
  FINISH_CREATING = 'finish_creating',
  FINISH_CREATING_LEASE_EXTENSION = 'finish_creating_lease_extension',
  FINISH_SIGNING = 'finish_signing',
  GO_TO_LEASES_HELP = 'go_to_leases_help',
  GO_TO_LEASES_TENANT_HELP = 'go_to_leases_tenant_help',
  MANAGE_EXPIRING_LEASE = 'manage_expiring_lease',
  MANAGE_EXPIRED_LEASE = 'manage_expired_lease',
  MANAGE_YOUR_LEASE = 'manage_your_lease',
  PREPARE_LEASE_FOR_SIGNING = 'prepare_lease_for_signing',
  PREPARE_LEASE_FOR_SIGNING_SUMMARY = 'prepare_lease_for_signing_summary',
  PREVIEW_FULL_LEASE = 'preview_full_lease',
  PREVIEW_FULL_LEASE_EXTENSION = 'preview_full_lease_extension',
  REGISTER_TO_VOTE = 'register_to_vote',
  REQUEST_SIGNATURES = 'request_signatures',
  REQUEST_SIGNATURES_LEASE_EXTENSION = 'request_signatures_lease_extension',
  REQUEST_SIGNATURES_SELF_UPLOAD = 'request_signatures_self_upload',
  REQUEST_SIGNATURES_BLOCKER = 'request_signatures_blocker',
  REQUEST_SIGNATURES_BLOCKER_LEASE_EXTENSION = 'request_signatures_blocker_lease_extension',
  REQUEST_ONLINE_SIGNATURES = 'request_online_signatures',
  RESEND_SIGNING_EMAILS = 'resend_signing_emails',
  RESEND_SIGNING_EMAILS_LEASE_EXTENSION = 'resend_signing_emails_lease_extension',
  SETUP_RENT_PAYMENT = 'setup_rent_payment',
  SIGN_LEASE = 'sign_lease',
  SIGNED_SELF_UPLOAD_DONE = 'signed_self_upload_done',
  START_SIGNING = 'start_signing',
  TERMINATE_LEASE = 'terminate_lease',
  VIEW_AND_BEGIN_COUNTERSIGN_LEASE = 'view_and_begin_countersign_lease',
  VIEW_AND_COUNTERSIGN_LEASE = 'view_and_countersign_lease',
  VIEW_AND_COUNTERSIGN_LEASE_EXTENSION = 'view_and_countersign_lease_extension',
  VIEW_AND_SIGN_FULL_LEASE = 'view_and_sign_full_lease',
  VIEW_AND_SIGN_FULL_LEASE_EXTENSION = 'view_and_sign_full_lease_extension',
  VIEW_AND_SIGN_LEASE = 'view_and_sign_lease',
  VIEW_AND_SIGN_LEASE_EXTENSION = 'view_and_sign_lease_extension',
  VIEW_FULL_LEASE = 'view_full_lease',
  VIEW_FULL_LEASE_EXTENSION = 'view_full_lease_extension',
  VIEW_EXPLAINER_LEASE = 'view_explainer_lease',
  VIEW_LEASE = 'view_lease',
  VIEW_LEASE_EXTENSION = 'view_lease_extension',
  VIEW_LEASE_FORWARD = 'view_lease_forward',
  VIEW_PREPARED_LEASE = 'view_prepared_lease',
  VIEW_PREVIOUS_VERSION = 'view_previous_version',
  VIEW_PREVIOUS_VERSION_LEASE_EXTENSION = 'view_previous_version_lease_extension',
  VIEW_SIGNED_LEASE = 'view_signed_lease',
  VIEW_SIGNED_LEASE_EXTENSION = 'view_signed_lease_extension',
  // *** THE DEFINITIONS HERE ARE USED DIRECTLY IN ANALYTICS TRACKING. BE CAREFUL WHEN MODIFYING ***
}

type ActionAnalytics =
  | ((args: LeaseListingUserEventArgs) => void)
  | {
      top: (args: LeaseListingUserEventArgs) => void;
      bottom: (args: LeaseListingUserEventArgs) => void;
    };

export type Action = {
  component?: string;
  doesNotCareIfLeaseStateHasChanged?: boolean;
  emitAnalyticsEvent?: ActionAnalytics;
  icon?: React.ReactNode;
  iconPosition?: string;
  label: string;
  mobileHandoffValue?: MOBILE_HANDOFF_TYPES;
  trackingEvent?: string | null;
};

export const ACTION_MAPPINGS: Partial<Record<LEASE_ACTION_TYPES, Action>> = Object.freeze({
  [LEASE_ACTION_TYPES.ARCHIVE_LEASE]: {
    emitAnalyticsEvent: emitDeleteDraftEvent,
    component: 'OpenModalArchiveLease',
    trackingEvent: null,
    label: 'Delete draft',
    icon: <IconDeleteOutline />,
  },
  [LEASE_ACTION_TYPES.ARCHIVE_LEASE_EXTENSION]: {
    component: 'OpenModalArchiveLeaseExtension',
    trackingEvent: null,
    label: 'Delete draft',
    icon: <IconDeleteOutline />,
  },
  [LEASE_ACTION_TYPES.BEGIN_COUNTERSIGN_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'Sign lease',
    icon: <IconDrawOutline />,
  },
  [LEASE_ACTION_TYPES.CANCEL_SIGNING]: {
    emitAnalyticsEvent: emitCancelSigningEvent,
    trackingEvent: null,
    component: 'OpenModalCancelSigning',
    label: 'Cancel signing',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.CANCEL_SIGNING,
    icon: <IconCloseCircleOutline />,
  },
  [LEASE_ACTION_TYPES.CANCEL_SIGNING_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalCancelSigningLeaseExtension',
    label: 'Cancel signing',
    icon: <IconCloseCircleOutline />,
  },
  [LEASE_ACTION_TYPES.COPY_LEASE]: {
    emitAnalyticsEvent: emitDuplicateLeaseEvent,
    trackingEvent: null,
    component: 'CopyLease',
    label: 'Duplicate',
    icon: <IconCopyOutline />,
  },
  [LEASE_ACTION_TYPES.DEACTIVATE_LEASE]: {
    emitAnalyticsEvent: emitDeactivateLeaseEvent,
    trackingEvent: null,
    label: 'Deactivate lease',
    component: 'OpenModalDeactivateLeaseConfirmation',
    icon: <IconLinkBrokenOutline />,
  },
  [LEASE_ACTION_TYPES.DELETE_LEASE]: {
    trackingEvent: null,
    label: 'Delete lease',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_DOCUMENT]: {
    trackingEvent: 'Preview-DraftDownload',
    component: 'DownloadDraftPdf',
    icon: <IconDownloadOutline />,
    label: 'Download document',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_DOCUMENT_LEASE_EXTENSION]: {
    component: 'DownloadDraftPdfLeaseExtension',
    icon: <IconDownloadOutline />,
    label: 'Download document',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_DRAFT_PDF]: {
    trackingEvent: 'Preview-DraftDownload',
    component: 'DownloadDraftPdf',
    icon: <IconDownloadOutline />,
    label: 'Download draft PDF',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_DRAFT_PDF,
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_DRAFT_PDF_LEASE_EXTENSION]: {
    component: 'DownloadDraftPdfLeaseExtension',
    icon: <IconDownloadOutline />,
    label: 'Download draft PDF',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_DRAFT_PDF,
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SHARE_INCOMPLETE_DRAFT]: {
    trackingEvent: null,
    component: 'OpenModalDownloadShareDraft',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_SHARE_DRAFT,
    icon: <IconShareWebOutline />,
    label: 'Download/share draft',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SHARE_INCOMPLETE_DRAFT_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalDownloadShareDraftLeaseExtension',
    icon: <IconShareWebOutline />,
    label: 'Download/share draft',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SHARE_COMPLETE_DRAFT]: {
    trackingEvent: null,
    component: 'OpenModalDownloadShareDraft',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_SHARE_DRAFT,
    icon: <IconShareWebOutline />,
    label: 'Download/share draft',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SHARE_COMPLETE_DRAFT_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalDownloadShareDraftLeaseExtension',
    icon: <IconShareWebOutline />,
    label: 'Download/share draft',
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SIGNABLE_PDF]: {
    trackingEvent: null,
    component: 'DownloadLeasePdf',
    icon: <IconDownloadOutline />,
    label: 'Download signable PDF',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_PDF,
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SIGNED_PDF]: {
    emitAnalyticsEvent: emitDownloadSignedPdfEvent,
    trackingEvent: null,
    component: 'DownloadLeasePdf',
    icon: <IconDownloadOutline />,
    label: 'Download signed PDF',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_PDF,
  },
  [LEASE_ACTION_TYPES.DOWNLOAD_SIGNED_PDF_LEASE_EXTENSION]: {
    component: 'DownloadLeaseExtensionPdf',
    icon: <IconDownloadOutline />,
    label: 'Download signed PDF',
  },
  [LEASE_ACTION_TYPES.EDIT_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToSummary',
    label: 'Edit lease',
    icon: <IconEditOutline />,
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.LEASE_EDIT,
  },
  [LEASE_ACTION_TYPES.EDIT_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseRenewalCosmosSummary',
    label: 'Edit lease renewal',
    icon: <IconEditOutline />,
  },
  [LEASE_ACTION_TYPES.EDIT_SIGNATURES]: {
    emitAnalyticsEvent: emitEditZdocsEvent,
    trackingEvent: null,
    component: 'OpenEditBlocker',
    label: 'Edit',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.LEASE_EDIT,
  },
  [LEASE_ACTION_TYPES.EDIT_VIEW_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToSummary',
    label: 'Edit/view lease',
    icon: <IconEditOutline />,
  },
  [LEASE_ACTION_TYPES.EDIT_VIEW_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseRenewalCosmosSummary',
    label: 'Edit lease renewal',
    icon: <IconEditOutline />,
  },
  [LEASE_ACTION_TYPES.FINISH_CREATING]: {
    emitAnalyticsEvent: emitFinishCreatingLeaseEvent,
    trackingEvent: null,
    component: 'RedirectToLeaseRoot',
    label: 'Finish creating',
  },
  [LEASE_ACTION_TYPES.FINISH_CREATING_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseRenewalCosmosSummary',
    label: 'Finish creating',
  },
  [LEASE_ACTION_TYPES.FINISH_SIGNING]: {
    trackingEvent: null,
    component: 'FinishSigningExperience',
    label: 'Finish',
  },
  [LEASE_ACTION_TYPES.PREPARE_LEASE_FOR_SIGNING_SUMMARY]: {
    emitAnalyticsEvent: { top: emitNextTopEvent, bottom: emitNextBottomEvent },
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'Next',
  },
  [LEASE_ACTION_TYPES.PREPARE_LEASE_FOR_SIGNING]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'Prepare lease for signing',
  },
  [LEASE_ACTION_TYPES.PREVIEW_FULL_LEASE]: {
    emitAnalyticsEvent: { top: emitPreviewFullLeaseTopEvent, bottom: emitPreviewFullLeaseBottomEvent },
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'Preview full lease',
  },
  [LEASE_ACTION_TYPES.PREVIEW_FULL_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'Preview lease renewal',
  },
  [LEASE_ACTION_TYPES.REQUEST_SIGNATURES]: {
    emitAnalyticsEvent: emitRequestSignatureZdocsEvent,
    trackingEvent: null,
    component: 'OpenModalRequestSignature',
    label: 'Request signatures',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.REQUEST_SIGNATURE,
    icon: <IconSignatureOutline />,
  },
  [LEASE_ACTION_TYPES.REQUEST_SIGNATURES_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalRequestSignatureLeaseExtension',
    label: 'Request signatures',
    icon: <IconSignatureOutline />,
  },
  [LEASE_ACTION_TYPES.REQUEST_SIGNATURES_BLOCKER]: {
    trackingEvent: null,
    component: 'OpenModalRequestSignaturesBlocker',
    label: 'Request signatures',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.REQUEST_SIGNATURE_BLOCKER,
    icon: <IconSignatureOutline />,
  },
  [LEASE_ACTION_TYPES.REQUEST_SIGNATURES_BLOCKER_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalRequestSignaturesLeaseExtensionBlocker',
    label: 'Request signatures',
    icon: <IconSignatureOutline />,
  },
  [LEASE_ACTION_TYPES.REQUEST_ONLINE_SIGNATURES]: {
    emitAnalyticsEvent: emitRequestSignatureZdocsEvent,
    trackingEvent: null,
    component: 'OpenModalRequestSignature',
    label: 'Request signatures',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.REQUEST_SIGNATURE,
    icon: <IconSignatureOutline />,
  },
  [LEASE_ACTION_TYPES.COUNTERSIGN_LEASE]: {
    trackingEvent: null,
    component: 'OpenModalCountersign',
    label: 'Sign lease',
  },
  [LEASE_ACTION_TYPES.COUNTERSIGN_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'OpenModalCountersignLeaseExtension',
    label: 'Sign lease renewal',
  },
  [LEASE_ACTION_TYPES.GO_TO_LEASES_HELP]: {
    trackingEvent: 'Preview-LeasesHelp',
    component: 'OpenTabHelpLink',
    doesNotCareIfLeaseStateHasChanged: true,
    label: 'Get lease help',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.LEASE_HELP,
    icon: <IconExternalOutline />,
  },
  [LEASE_ACTION_TYPES.GO_TO_LEASES_TENANT_HELP]: {
    trackingEvent: 'Preview-LeasesHelp',
    component: 'OpenTabTenantHelpLink',
    doesNotCareIfLeaseStateHasChanged: true,
    label: 'Get lease help',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.LEASE_HELP,
    icon: <IconExternalOutline />,
  },
  [LEASE_ACTION_TYPES.SETUP_RENT_PAYMENT]: {
    emitAnalyticsEvent: emitSetupRentPaymentEvent,
    trackingEvent: null,
    component: 'RedirectToPayments',
    icon: <IconPiggyBankOutline />,
    label: 'Set up rent payment',
  },
  [LEASE_ACTION_TYPES.SIGN_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'Sign lease',
  }, // Below action can't be used ATM due to the way how modals are triggered in states.
  [LEASE_ACTION_TYPES.SIGNED_SELF_UPLOAD_DONE]: {
    trackingEvent: null,
    component: 'FinalizeSignedSelfUpload',
    label: 'Finalize lease',
  },
  [LEASE_ACTION_TYPES.START_SIGNING]: {
    trackingEvent: null,
    label: 'Start signing',
    component: 'StartSigningExperience',
  },
  [LEASE_ACTION_TYPES.TERMINATE_LEASE]: {
    trackingEvent: null,
    component: 'OpenModalCancelActive',
    label: 'Cancel lease',
    icon: <IconCloseCircleOutline />,
  },
  [LEASE_ACTION_TYPES.VIEW_AND_SIGN_LEASE]: {
    emitAnalyticsEvent: emitViewAndSignLeaseEvent,
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View and sign lease',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_SIGN_LEASE_EXTENSION]: {
    emitAnalyticsEvent: emitViewAndSignLeaseEvent,
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'Sign lease renewal',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_COUNTERSIGN_LEASE]: {
    trackingEvent: null,
    component: 'OpenModalCountersign',
    label: 'View and sign lease',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_COUNTERSIGN_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'Sign lease renewal',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_BEGIN_COUNTERSIGN_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View and sign lease',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_SIGN_FULL_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View (and sign) full lease',
  },
  [LEASE_ACTION_TYPES.VIEW_AND_SIGN_FULL_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'View (and sign) full lease renewal',
  },
  [LEASE_ACTION_TYPES.VIEW_FULL_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View full lease',
  },
  [LEASE_ACTION_TYPES.VIEW_FULL_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'View full lease renewal',
  },
  [LEASE_ACTION_TYPES.VIEW_EXPLAINER_LEASE]: {
    trackingEvent: 'Preview-ExplainerLease',
    component: 'OpenTabExplainerLease',
    doesNotCareIfLeaseStateHasChanged: true,
    label: 'Show explainer lease',
    mobileHandoffValue: MOBILE_HANDOFF_TYPES.DOWNLOAD_EXPLAINER_LEASE,
    icon: <IconFileOutline />,
  },
  [LEASE_ACTION_TYPES.VIEW_LEASE]: {
    emitAnalyticsEvent: emitViewLeaseEvent,
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View lease',
    icon: <IconFileOutline />,
  },
  [LEASE_ACTION_TYPES.VIEW_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'View lease renewal',
    icon: <IconFileOutline />,
  },
  [LEASE_ACTION_TYPES.VIEW_LEASE_FORWARD]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View lease',
    icon: <IconChevronRightOutline />,
    iconPosition: 'end',
  },
  [LEASE_ACTION_TYPES.VIEW_PREPARED_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View prepared lease',
    icon: <IconFileOutline />,
    iconPosition: 'end',
  },
  [LEASE_ACTION_TYPES.VIEW_PREVIOUS_VERSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View previous version',
  },
  [LEASE_ACTION_TYPES.VIEW_PREVIOUS_VERSION_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'View previous version',
  },
  [LEASE_ACTION_TYPES.VIEW_SIGNED_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseView',
    label: 'View signed lease',
  },
  [LEASE_ACTION_TYPES.VIEW_SIGNED_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPDFView',
    label: 'View signed lease renewal',
  },
  [LEASE_ACTION_TYPES.RESEND_SIGNING_EMAILS]: {
    emitAnalyticsEvent: emitResendSigningEmailsEvent,
    trackingEvent: null,
    component: 'ResendSigningEmails',
    label: 'Resend emails for signing',
    icon: <IconSendOutline />,
  },
  [LEASE_ACTION_TYPES.RESEND_SIGNING_EMAILS_LEASE_EXTENSION]: {
    trackingEvent: null,
    component: 'ResendSigningEmailsLeaseExtension',
    label: 'Resend emails for signing',
    icon: <IconSendOutline />,
  },
  [LEASE_ACTION_TYPES.REGISTER_TO_VOTE]: {
    trackingEvent: null,
    component: 'OpenTabRegisterToVote',
    label: 'Register to vote',
    doesNotCareIfLeaseStateHasChanged: true,
    icon: <IconExternalOutline />,
  },
  [LEASE_ACTION_TYPES.CREATE_PROPERTY_CONDITION_REPORT]: {
    trackingEvent: null,
    component: 'RedirectToPropertyConditionReport',
    icon: <IconChecklistOutline />,
    label: 'Create a property condition report',
  },
  [LEASE_ACTION_TYPES.MANAGE_EXPIRING_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPreRenewalWizard',
    icon: <IconCalendarClockOutline />,
    label: 'Manage expiring lease',
  },
  [LEASE_ACTION_TYPES.MANAGE_EXPIRED_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPreRenewalWizard',
    icon: <IconCalendarClockOutline />,
    label: 'Manage expired lease',
  },
  [LEASE_ACTION_TYPES.MANAGE_YOUR_LEASE]: {
    trackingEvent: null,
    component: 'RedirectToLeaseExtensionPreRenewalWizard',
    icon: <IconCalendarClockOutline />,
    label: 'Manage your lease',
  },
});
